import { Injectable, inject } from '@angular/core';
import { Observable, map, of, tap } from 'rxjs';
import { ApiService } from '../../../shared/services/api.service';
import {
  ICrossFieldRule,
  ICrossFieldRuleCreate,
  ICrossFieldRulePatch,
  IRuleFilters,
  IRuleOperation,
  IRuleParamDefinition,
  IRuleParamDefinitionCreate,
  IRuleParamDefinitionPatch,
  IRuleTypeCatalogEntry,
  IRuleTypeCatalogResponse,
  IRuleTypeDefinition,
  IRuleTypeDefinitionPayload,
  IValidatorRule,
  IValidatorRuleCreate,
  IValidatorRulePatch,
} from '../models/validation-rule';

/**
 * Thin wrapper over the three `/api/metadata-validations/` endpoints.
 *
 * Catalog (`GET /rule-types/`) is fetched at most once per session; the
 * response is held in a private field so multiple consumers can subscribe
 * without re-hitting the network.
 */
@Injectable({
  providedIn: 'root',
})
export class ValidationRulesService {
  private readonly apiService = inject(ApiService);

  readonly fieldRulesUrl = 'api/metadata-validations/rules';
  readonly crossFieldRulesUrl = 'api/metadata-validations/cross-field-rules';
  readonly ruleTypesUrl = 'api/metadata-validations/rule-types';
  readonly operationsUrl = 'api/metadata-validations/operations';
  readonly ruleTypeDefsUrl = 'api/metadata-validations/rule-type-definitions';
  readonly ruleParamDefsUrl = 'api/metadata-validations/rule-param-definitions';

  private cachedCatalog: IRuleTypeCatalogEntry[] | null = null;

  // ---- field-level rules -----------------------------------------------

  public getFieldRules(filters: IRuleFilters = {}): Observable<IValidatorRule[]> {
    const url = this.apiService.buildFullURL(this.fieldRulesUrl, this.toQuery(filters));
    return this.apiService.get<IValidatorRule[]>(url, { withCredentials: true });
  }

  public createFieldRule(payload: IValidatorRuleCreate): Observable<IValidatorRule> {
    const url = this.apiService.buildFullURL(this.fieldRulesUrl, '');
    return this.apiService.post<IValidatorRule>(url, payload, { withCredentials: true });
  }

  public patchFieldRule(id: number, changes: IValidatorRulePatch): Observable<IValidatorRule> {
    const url = this.apiService.buildFullURL(this.fieldRulesUrl, `${id}/`);
    return this.apiService.patch<IValidatorRule>(url, changes, { withCredentials: true });
  }

  public deleteFieldRule(id: number): Observable<unknown> {
    const url = this.apiService.buildFullURL(this.fieldRulesUrl, `${id}/`);
    return this.apiService.delete(url, { withCredentials: true });
  }

  // ---- cross-field rules ----------------------------------------------

  public getCrossFieldRules(filters: IRuleFilters = {}): Observable<ICrossFieldRule[]> {
    const url = this.apiService.buildFullURL(this.crossFieldRulesUrl, this.toQuery(filters));
    return this.apiService.get<ICrossFieldRule[]>(url, { withCredentials: true });
  }

  public createCrossFieldRule(payload: ICrossFieldRuleCreate): Observable<ICrossFieldRule> {
    const url = this.apiService.buildFullURL(this.crossFieldRulesUrl, '');
    return this.apiService.post<ICrossFieldRule>(url, payload, { withCredentials: true });
  }

  public patchCrossFieldRule(id: number, changes: ICrossFieldRulePatch): Observable<ICrossFieldRule> {
    const url = this.apiService.buildFullURL(this.crossFieldRulesUrl, `${id}/`);
    return this.apiService.patch<ICrossFieldRule>(url, changes, { withCredentials: true });
  }

  public deleteCrossFieldRule(id: number): Observable<unknown> {
    const url = this.apiService.buildFullURL(this.crossFieldRulesUrl, `${id}/`);
    return this.apiService.delete(url, { withCredentials: true });
  }

  // ---- rule-type catalog ----------------------------------------------

  /**
   * Cached fetch of the rule-types catalog. The catalog is small (one row
   * per registered rule type) and rarely changes mid-session, so a single
   * in-memory copy is plenty.
   */
  public getRuleTypeCatalog(): Observable<IRuleTypeCatalogEntry[]> {
    if (this.cachedCatalog) {
      return of(this.cachedCatalog);
    }
    const url = this.apiService.buildFullURL(this.ruleTypesUrl, '');
    return this.apiService.get<IRuleTypeCatalogResponse>(url, { withCredentials: true }).pipe(
      tap((res) => (this.cachedCatalog = res.rule_types)),
      map((res) => res.rule_types),
    );
  }

  /**
   * Force-refresh the catalog (e.g. after a backend deploy adds rule types).
   * Not wired to UI in v1; exposed for tests and future tooling.
   */
  public refreshRuleTypeCatalog(): void {
    this.cachedCatalog = null;
  }

  // ---- configurable catalog: ops + rule-type definitions ----------

  public getOperations(kind?: 'field' | 'cross'): Observable<IRuleOperation[]> {
    const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
    const url = this.apiService.buildFullURL(this.operationsUrl, q);
    return this.apiService.get<IRuleOperation[]>(url, { withCredentials: true });
  }

  public getRuleTypeDefinitions(filters: { kind?: string; is_active?: boolean } = {}): Observable<IRuleTypeDefinition[]> {
    const parts: string[] = [];
    if (filters.kind !== undefined) parts.push(`kind=${encodeURIComponent(filters.kind)}`);
    if (filters.is_active !== undefined) parts.push(`is_active=${filters.is_active}`);
    const url = this.apiService.buildFullURL(
      this.ruleTypeDefsUrl,
      parts.length ? `?${parts.join('&')}` : '',
    );
    return this.apiService.get<IRuleTypeDefinition[]>(url, { withCredentials: true });
  }

  public createRuleTypeDefinition(payload: IRuleTypeDefinitionPayload & { code: string }): Observable<IRuleTypeDefinition> {
    const url = this.apiService.buildFullURL(this.ruleTypeDefsUrl, '');
    return this.apiService.post<IRuleTypeDefinition>(url, payload, { withCredentials: true });
  }

  public patchRuleTypeDefinition(id: number, changes: Partial<IRuleTypeDefinitionPayload>): Observable<IRuleTypeDefinition> {
    const url = this.apiService.buildFullURL(this.ruleTypeDefsUrl, `${id}/`);
    return this.apiService.patch<IRuleTypeDefinition>(url, changes, { withCredentials: true });
  }

  public deleteRuleTypeDefinition(id: number): Observable<unknown> {
    const url = this.apiService.buildFullURL(this.ruleTypeDefsUrl, `${id}/`);
    return this.apiService.delete(url, { withCredentials: true });
  }

  public createParamDefinition(payload: IRuleParamDefinitionCreate): Observable<IRuleParamDefinition> {
    const url = this.apiService.buildFullURL(this.ruleParamDefsUrl, '');
    return this.apiService.post<IRuleParamDefinition>(url, payload, { withCredentials: true });
  }

  public patchParamDefinition(id: number, changes: IRuleParamDefinitionPatch): Observable<IRuleParamDefinition> {
    const url = this.apiService.buildFullURL(this.ruleParamDefsUrl, `${id}/`);
    return this.apiService.patch<IRuleParamDefinition>(url, changes, { withCredentials: true });
  }

  public deleteParamDefinition(id: number): Observable<unknown> {
    const url = this.apiService.buildFullURL(this.ruleParamDefsUrl, `${id}/`);
    return this.apiService.delete(url, { withCredentials: true });
  }

  // ---- helpers ---------------------------------------------------------

  private toQuery(filters: IRuleFilters): string {
    const parts: string[] = [];
    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null) continue;
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
    return parts.length ? `?${parts.join('&')}` : '';
  }
}
